import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Modal, Row, Spinner, Table, Alert } from 'react-bootstrap';
import QtyDual from '../../utils/inventoryUnit';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../layouts/PageLayout';
import { purchaseOrderAPI, supplierOrderZaloAPI } from '../../services/purchaseOrder';
import { toast } from 'react-toastify';

const fmtQty = (n, unit = '') => `${Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 })}${unit ? ' ' + unit : ''}`;
const fmtPrice = (n) => n != null ? Number(n).toLocaleString('vi-VN') + 'đ' : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleString('vi-VN') : '—';

const STATUS_MAP = {
  DRAFT: { label: 'Nháp', bg: 'secondary' },
  CONFIRMED: { label: 'Đã xác nhận', bg: 'primary' },
  RECEIVING: { label: 'Đang kiểm', bg: 'warning' },
  COMPLETED: { label: 'Hoàn thành', bg: 'success' },
  CANCELLED: { label: 'Đã hủy', bg: 'danger' },
};

// #010639 — màu cho 4 mức phản hồi NCC (nhãn chữ do BE trả trong `level_label`, FE chỉ tô màu).
const NCC_RESPONSE_BG = {
  SENT: 'danger',
  OPENED: 'warning',
  CONFIRMED: 'success',
  CHANGE_PENDING: 'info',
  UNKNOWN: 'secondary',
};

// #141201 — nhãn + màu cho từng loại thao tác trong nhật ký kiểm kê. Chữ chính do BE trả
// (`action_label`) để 2 app không lệch nhau; map này chỉ tô màu và dự phòng khi thiếu nhãn.
const CHECK_ACTION_BG = {
  OPENED: 'primary',
  VIEWED: 'secondary',
  SUBMITTED: 'success',
  NOT_ARRIVED: 'warning',
  CANCELLED: 'danger',
  COMPLETED: 'success',
};

const GR_STATUS_MAP = {
  IN_PROGRESS: { label: 'Đang kiểm', bg: 'warning' },
  // #223048 — chỉ còn xuất hiện ở phiếu CŨ (phiếu mới chốt thẳng COMPLETED)
  PENDING_REVIEW: { label: 'Chờ duyệt (phiếu cũ)', bg: 'danger' },
  COMPLETED: { label: 'Hoàn thành', bg: 'success' },
  CANCELLED: { label: 'Đã huỷ', bg: 'secondary' },
};

/**
 * #141201 — tóm tắt payload nhật ký thành 1 dòng chữ cho quản lý đọc. Payload là JSON tự do nên
 * chỉ rút đúng vài trường có ý nghĩa nghiệp vụ, không đổ cả JSON thô ra bảng.
 */
function describeCheckEvent(e) {
  const p = e?.payload || {};
  switch (e?.action_type) {
    case 'OPENED':
      return [p.order_number ? `Đơn ${p.order_number}` : null, p.total_lines != null ? `${p.total_lines} dòng NVL` : null]
        .filter(Boolean).join(' · ') || '—';
    case 'SUBMITTED': {
      const soDongLech = Array.isArray(p.variance_lines) ? p.variance_lines.length : 0;
      return [
        p.has_variance ? `Có chênh lệch (${soDongLech} dòng)` : 'Khớp số đặt',
        p.note ? `Ghi chú: ${p.note}` : null,
      ].filter(Boolean).join(' · ');
    }
    case 'NOT_ARRIVED':
      return p.note ? `Ghi chú: ${p.note}` : 'Không ghi chú';
    case 'CANCELLED':
      return p.checker_name ? `NV kiểm: ${p.checker_name}` : '—';
    case 'VIEWED':
      return 'Mở phiếu lên xem (không sửa)';
    default:
      return '—';
  }
}

export default function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reviewing, setReviewing] = useState(null); // 'ACCEPT' | 'REJECT'
  const [cancellingGR, setCancellingGR] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  // Gửi đơn qua Zalo (#145947) — đơn này thuộc đúng 1 chi nhánh nên gửi đúng 1 tin.
  const [zaloPreview, setZaloPreview] = useState(null);
  const [zaloPreviewing, setZaloPreviewing] = useState(false);
  const [zaloSending, setZaloSending] = useState(false);
  const [zaloResult, setZaloResult] = useState(null);

  // #141201 — nhật ký thao tác + danh sách NV trực ca không hề đụng vào phiếu.
  const [checkEvents, setCheckEvents] = useState(null);
  const [noInteraction, setNoInteraction] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);

  const fetchPO = async () => {
    try {
      setLoading(true);
      const data = await purchaseOrderAPI.getReceiptView(id);
      setPo(data);
    } catch (err) {
      toast.error(err?.message || 'Không thể tải đơn đặt hàng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPO(); }, [id]);

  // #141201 — tra theo id PHIẾU KIỂM nếu còn phiếu, ngược lại theo id ĐƠN (phiếu đã bị huỷ/
  // "chưa đến" thì dòng GoodsReceipt không còn tồn tại, nhật ký vẫn còn và vẫn phải xem được).
  const fetchCheckHistory = async (key) => {
    try {
      setEventsLoading(true);
      const [ev, ni] = await Promise.all([
        purchaseOrderAPI.getGoodsReceiptEvents(key),
        purchaseOrderAPI.getGoodsReceiptNoInteraction(key),
      ]);
      setCheckEvents(ev);
      setNoInteraction(ni);
    } catch (err) {
      // Không toast: nhật ký là thông tin phụ trợ, hỏng thì không được che mất màn đơn hàng.
      setCheckEvents({ data: [], total: 0 });
      setNoInteraction(null);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (!po) return;
    fetchCheckHistory(po.goods_receipt?.id || po.id);
  }, [po?.id, po?.goods_receipt?.id]);

  const handleConfirm = async () => {
    if (!window.confirm('Xác nhận đơn đặt hàng này?')) return;
    try {
      setConfirming(true);
      await purchaseOrderAPI.confirm(id);
      toast.success('Đã xác nhận đơn đặt hàng');
      fetchPO();
    } catch (err) {
      toast.error(err?.message || 'Không thể xác nhận');
    } finally {
      setConfirming(false);
    }
  };

  // NCC của đơn: mỗi dòng NVL có supplier_id riêng, nhưng 1 tin chỉ gửi cho 1 NCC.
  // Lấy tập NCC thật có trong đơn để chặn trường hợp đơn trộn nhiều NCC.
  const orderSuppliers = React.useMemo(() => {
    const map = new Map();
    (po?.items || []).forEach((i) => {
      if (i.supplier?.id) map.set(i.supplier.id, i.supplier);
    });
    return [...map.values()];
  }, [po]);

  const buildZaloPayload = () => ({
    supplier_id: orderSuppliers[0]?.id,
    purchase_order_id: po.id,
    branches: [
      {
        branch_id: po.branch_id,
        note: po.note || undefined,
        items: (po.items || [])
          .filter((i) => i.supplier?.id === orderSuppliers[0]?.id)
          .map((i) => ({
            material_id: i.material_id,
            package_id: i.package?.id || undefined,
            quantity: Number(i.package_quantity),
          }))
          .filter((i) => i.quantity > 0),
      },
    ],
  });

  const handleZaloPreview = async () => {
    if (orderSuppliers.length !== 1) {
      toast.error(
        orderSuppliers.length === 0
          ? 'Đơn chưa gán nhà cung cấp cho NVL nào — không biết gửi cho ai'
          : 'Đơn có nhiều NCC. Dùng màn "Gửi đơn qua Zalo" để gửi tách theo từng NCC.',
      );
      return;
    }
    try {
      setZaloPreviewing(true);
      setZaloResult(null);
      const payload = buildZaloPayload();
      const res = await supplierOrderZaloAPI.preview(payload);
      setZaloPreview({ ...res, payload });
    } catch (err) {
      toast.error(err?.message || 'Không dựng được nội dung tin');
    } finally {
      setZaloPreviewing(false);
    }
  };

  const handleZaloSend = async () => {
    if (!zaloPreview?.payload) return;
    try {
      setZaloSending(true);
      const res = await supplierOrderZaloAPI.send(zaloPreview.payload);
      setZaloResult(res);
      // Có tin nào hỏng là báo lỗi rõ, không nuốt lỗi báo thành công.
      if (res.failed > 0) toast.error(`Gửi thất bại: ${res.results.find((r) => !r.ok)?.error || 'không rõ lý do'}`);
      else toast.success(`Đã gửi ${res.sent} tin đơn đặt hàng qua Zalo`);
      setZaloPreview(null);
    } catch (err) {
      toast.error(err?.message || 'Không gửi được đơn qua Zalo');
    } finally {
      setZaloSending(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Hủy đơn đặt hàng này?')) return;
    try {
      setCancelling(true);
      await purchaseOrderAPI.cancel(id);
      toast.success('Đã hủy đơn');
      fetchPO();
    } catch (err) {
      toast.error(err?.message || 'Không thể hủy');
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelGR = async () => {
    if (!window.confirm('Huỷ phiếu kiểm hàng này?\nPhiếu sẽ bị xoá, đơn hàng quay về trạng thái Đã xác nhận và có thể kiểm lại.')) return;
    try {
      setCancellingGR(true);
      const result = await purchaseOrderAPI.cancelGoodsReceipt(po.goods_receipt.id);
      toast.success(result?.message || 'Đã huỷ phiếu kiểm hàng');
      fetchPO();
    } catch (err) {
      toast.error(err?.message || 'Không thể huỷ phiếu kiểm');
    } finally {
      setCancellingGR(false);
    }
  };

  const handleRollback = async () => {
    const hasCashVoucher = po.pre_paid && po.cash_voucher_id;
    const warning = hasCashVoucher
      ? '\n\n⚠️ Đơn này đã thanh toán trước. Sau khi rollback cần xử lý phiếu chi thủ công.'
      : '';
    const reason = window.prompt(`Nhập lý do rollback nhập kho (bắt buộc):${warning}`);
    if (!reason?.trim()) return;
    try {
      setRollingBack(true);
      const result = await purchaseOrderAPI.rollbackGoodsReceipt(po.goods_receipt.id, reason);
      if (result?.has_cash_voucher_warning) {
        toast.warn('Rollback thành công. Lưu ý: đơn này đã thanh toán trước — cần xử lý phiếu chi thủ công!', { autoClose: 8000 });
      } else {
        toast.success(result?.message || 'Đã rollback, tồn kho đã được hoàn lại');
      }
      fetchPO();
    } catch (err) {
      toast.error(err?.message || 'Không thể rollback');
    } finally {
      setRollingBack(false);
    }
  };

  const handleReview = async (action) => {
    const msg = action === 'ACCEPT'
      ? 'Chấp nhận chênh lệch và nhập kho?'
      : 'Yêu cầu nhân viên kiểm lại hàng?';
    if (!window.confirm(msg)) return;
    try {
      setReviewing(action);
      await purchaseOrderAPI.reviewGoodsReceipt(po.goods_receipt.id, action);
      toast.success(action === 'ACCEPT' ? 'Đã nhập kho' : 'Đã yêu cầu kiểm lại');
      fetchPO();
    } catch (err) {
      toast.error(err?.message || 'Không thể xét duyệt');
    } finally {
      setReviewing(null);
    }
  };

  if (loading) return <PageLayout><div className="text-center py-5"><Spinner /></div></PageLayout>;
  if (!po) return <PageLayout><p className="text-center py-5 text-danger">Không tìm thấy đơn</p></PageLayout>;

  const status = STATUS_MAP[po.status] ?? { label: po.status, bg: 'secondary' };
  const gr = po.goods_receipt;
  const grStatus = gr ? (GR_STATUS_MAP[gr.status] ?? { label: gr.status, bg: 'secondary' }) : null;

  return (
    <PageLayout>
      <div className="container-fluid py-3" style={{ maxWidth: 1100 }}>
        {/* Header */}
        <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>← Quay lại</Button>
          <h4 className="mb-0 fw-bold">{po.order_number}</h4>
          <Badge bg={status.bg}>{status.label}</Badge>
          {gr && <Badge bg={grStatus.bg}>{grStatus.label}</Badge>}
          {/* #010639 — NCC đã xác nhận sẽ giao hay chưa, ngay cạnh trạng thái đơn: người mở chi
              tiết đơn để quyết định có gọi thúc không thì không phải quay lại bảng danh sách. */}
          {(po.supplier_responses || []).map((r) => (
            <Badge
              key={r.supplier_id}
              bg={NCC_RESPONSE_BG[r.level] ?? 'secondary'}
              title={r.confirmed_at ? `Xác nhận lúc ${new Date(r.confirmed_at).toLocaleString('vi-VN')}` : undefined}
            >
              {r.supplier_name}: {r.level_label}
            </Badge>
          ))}
          <div className="ms-auto d-flex gap-2">
            {po.status !== 'CANCELLED' && (
              <Button
                variant="outline-success"
                size="sm"
                data-testid="btn-zalo-preview"
                onClick={handleZaloPreview}
                disabled={zaloPreviewing}
              >
                {zaloPreviewing ? <Spinner size="sm" /> : '📩 Gửi đơn qua Zalo'}
              </Button>
            )}
            {po.status === 'DRAFT' && (
              <>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleConfirm}
                  disabled={confirming}
                >
                  {confirming ? <Spinner size="sm" /> : '✓ Xác nhận đặt hàng'}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  Hủy đơn
                </Button>
              </>
            )}
            {['CONFIRMED', 'RECEIVING'].includes(po.status) && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleCancel}
                disabled={cancelling}
              >
                Hủy đơn
              </Button>
            )}
          </div>
        </div>

        <Row className="g-3">
          {/* Info card */}
          <Col md={4}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <dl className="mb-0 small">
                  <dt className="text-muted">Chi nhánh</dt>
                  <dd className="fw-semibold">{po.branch?.name}</dd>
                  <dt className="text-muted">Người tạo</dt>
                  <dd>{po.creator?.full_name}</dd>
                  <dt className="text-muted">Ngày tạo</dt>
                  <dd>{fmtDate(po.created_at)}</dd>
                  {po.expected_delivery_date && (
                    <>
                      <dt className="text-muted">Ngày giao dự kiến</dt>
                      <dd>{fmtDate(po.expected_delivery_date)}</dd>
                    </>
                  )}
                  <dt className="text-muted">Tổng tiền</dt>
                  <dd className="text-success fw-bold fs-6">{fmtPrice(po.total_amount)}</dd>
                  {po.pre_paid && (
                    <>
                      <dt className="text-muted">Thanh toán</dt>
                      <dd>
                        <Badge bg="success">Đã thanh toán trước</Badge>
                        {po.cash_voucher && (
                          <div className="mt-1 small text-muted">Phiếu chi: {po.cash_voucher.voucher_number}</div>
                        )}
                      </dd>
                    </>
                  )}
                  {po.note && (
                    <>
                      <dt className="text-muted">Ghi chú</dt>
                      <dd>{po.note}</dd>
                    </>
                  )}
                </dl>
              </Card.Body>
            </Card>
          </Col>

          {/* PO items */}
          <Col md={8}>
            <Card className="shadow-sm mb-3">
              <Card.Header className="fw-semibold bg-white">Danh sách NVL đặt hàng</Card.Header>
              <Card.Body className="p-0">
                <Table size="sm" bordered hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>NVL</th>
                      <th>NCC</th>
                      <th className="text-end">SL đặt</th>
                      <th className="text-end">Giá</th>
                      <th className="text-end">Thành tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items?.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-medium">{item.material?.name}</div>
                          <small className="text-muted">{item.package?.unit_label ?? item.package?.name}</small>
                        </td>
                        <td className="text-muted small">{item.supplier?.name ?? '—'}</td>
                        <td className="text-end">
                          {fmtQty(item.package_quantity)} {item.package?.unit_label ?? item.package?.name}
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            = {fmtQty(item.quantity_in_base)} {item.material?.unit}
                          </div>
                        </td>
                        <td className="text-end small">{fmtPrice(item.unit_price)}</td>
                        <td className="text-end fw-medium">{fmtPrice(item.line_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* Goods Receipt section */}
            {gr && (
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold bg-white d-flex justify-content-between align-items-center">
                  <span>Phiếu kiểm hàng: {gr.receipt_number}</span>
                  <Badge bg={grStatus.bg}>{grStatus.label}</Badge>
                </Card.Header>
                <Card.Body>
                  <div className="small text-muted mb-3">
                    NV kiểm: <strong>{gr.checker?.full_name}</strong>
                    {gr.reviewer && <> · Xét duyệt: <strong>{gr.reviewer?.full_name}</strong> lúc {fmtDate(gr.reviewed_at)}</>}
                  </div>

                  {/* #201201 — phiếu chi gắn với phiếu kiểm này (lệnh sếp update_id=731599485).
                      Chỉ hiển thị liên kết chứng từ; tồn kho không phụ thuộc mục này. */}
                  {gr.cash_voucher && (
                    <div className="mb-3 p-2 border border-success rounded bg-light">
                      <div className="text-success small fw-semibold mb-1">💸 Phiếu chi liên kết</div>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Số phiếu:</span>
                        <span className="fw-semibold">{gr.cash_voucher.voucher_number}</span>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Số tiền:</span>
                        <span className="fw-semibold">{fmtPrice(gr.cash_voucher.amount)}</span>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Trạng thái:</span>
                        <Badge bg={
                          gr.cash_voucher.status === 'APPROVED' ? 'success' :
                          gr.cash_voucher.status === 'REJECTED' ? 'danger' :
                          'warning'
                        }>
                          {gr.cash_voucher.status === 'APPROVED' ? 'Đã duyệt' :
                           gr.cash_voucher.status === 'REJECTED' ? 'Bị từ chối' :
                           gr.cash_voucher.status === 'PENDING' ? 'Chờ duyệt' :
                           gr.cash_voucher.status}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Ngày chi:</span>
                        <span>{fmtDate(gr.cash_voucher.voucher_date)}</span>
                      </div>
                    </div>
                  )}

                  {/* Nút quản lý phiếu kiểm */}
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    {['IN_PROGRESS', 'PENDING_REVIEW'].includes(gr.status) && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleCancelGR}
                        disabled={cancellingGR}
                        title="Huỷ phiếu kiểm, đơn hàng quay về Đã xác nhận"
                      >
                        {cancellingGR ? <Spinner size="sm" /> : '🚫 Huỷ phiếu kiểm'}
                      </Button>
                    )}
                    {gr.status === 'COMPLETED' && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={handleRollback}
                        disabled={rollingBack}
                        title="Hoàn lại tồn kho đã cộng, đơn hàng quay về Đã xác nhận"
                      >
                        {rollingBack ? <Spinner size="sm" /> : '♻️ Rollback nhập kho'}
                      </Button>
                    )}
                  </div>

                  {/*
                    #223048 (lệnh sếp update_id=731599168, 28/08/2026) — bỏ cổng duyệt lệch SL.
                    Phiếu MỚI không bao giờ vào PENDING_REVIEW nữa; khối này chỉ còn để DỌN NỐT
                    các phiếu đã treo từ trước ngày phát hành. Đừng xoá khi tồn đọng chưa hết —
                    xoá là phiếu cũ kẹt vĩnh viễn, không còn đường nhập kho.
                  */}
                  {gr.status === 'PENDING_REVIEW' && (
                    <Alert variant="warning" className="py-2">
                      <strong>⚠️ Phiếu CŨ còn treo chờ duyệt chênh lệch</strong>
                      <div className="small mt-1">
                        Từ 28/08/2026 lệch SL không còn phải duyệt. Phiếu này lập trước đó nên vẫn
                        đang chờ — bấm "Chấp nhận &amp; nhập kho" để dọn nốt.
                      </div>
                      <div className="mt-2 d-flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleReview('ACCEPT')}
                          disabled={!!reviewing}
                        >
                          {reviewing === 'ACCEPT' ? <Spinner size="sm" /> : '✓ Chấp nhận & nhập kho'}
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleReview('REJECT')}
                          disabled={!!reviewing}
                        >
                          {reviewing === 'REJECT' ? <Spinner size="sm" /> : '↩ Yêu cầu kiểm lại'}
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {/* #223048 — phiếu đã chốt CÓ lệch: nêu rõ đã nhập theo số thực nhận + đã báo admin */}
                  {gr.status === 'COMPLETED' && gr.has_variance && (
                    <Alert variant="info" className="py-2">
                      <strong>ℹ️ Nhập kho có chênh lệch — đã ghi nhận theo SỐ THỰC NHẬN</strong>
                      <div className="small mt-1">
                        Không cần duyệt (lệnh sếp 28/08/2026). Thông báo đã gửi nhóm Telegram nhập
                        hàng. Số sai thì dùng "♻️ Rollback nhập kho" hoặc chức năng sửa SL đã nhận.
                      </div>
                    </Alert>
                  )}

                  {gr.items?.length > 0 && (
                    <Table size="sm" bordered responsive>
                      <thead className="table-light">
                        <tr>
                          <th>NVL</th>
                          <th className="text-end">SL đặt</th>
                          <th className="text-end">SL kiểm thực tế</th>
                          <th className="text-end">Chênh lệch</th>
                          <th>Lý do</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gr.items.map((item) => {
                          const variance = Number(item.variance ?? 0);
                          const ordered = Number(item.purchase_order_item?.quantity_in_base ?? 0);
                          const received = Number(item.received_quantity_base ?? 0);
                          const hasVar = Math.abs(variance) > 0.001;
                          return (
                            <tr key={item.id} className={hasVar ? 'table-warning' : ''}>
                              <td className="fw-medium">{item.material?.name}</td>
                              <td className="text-end">
                                <QtyDual quantityInBase={ordered} material={item.material} packageOverride={item.package} />
                              </td>
                              <td className="text-end">
                                <QtyDual quantityInBase={received} material={item.material} packageOverride={item.package} />
                              </td>
                              <td className={`text-end fw-bold ${hasVar ? 'text-danger' : 'text-success'}`}>
                                {hasVar ? (
                                  <>{variance > 0 ? '+' : ''}<QtyDual quantityInBase={Math.abs(variance)} material={item.material} packageOverride={item.package} /></>
                                ) : '✓'}
                              </td>
                              <td className="small text-muted">{item.variance_reason ?? '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}

                  {gr.note && (
                    <p className="small text-muted mt-2">Ghi chú NV: {gr.note}</p>
                  )}
                </Card.Body>
              </Card>
            )}

            {/*
              #141201 — LỊCH SỬ THAO TÁC phiếu kiểm kê nhập kho (sếp góp ý qua nút Trợ năng
              13:58 06/09/2026). Đặt NGAY dưới khối phiếu kiểm — đúng chỗ sếp đang đứng khi góp
              ý, không bắt qua màn khác. Khối này hiện CẢ KHI phiếu đã bị huỷ/"chưa đến" (lúc đó
              dòng GoodsReceipt đã bị xoá, nhật ký là dấu vết duy nhất còn lại).
            */}
            <Card className="shadow-sm mt-3" data-testid="check-history-card">
              <Card.Header className="fw-semibold bg-white">🕘 Lịch sử thao tác</Card.Header>
              <Card.Body>
                {eventsLoading && <div className="text-center py-3"><Spinner size="sm" /></div>}

                {!eventsLoading && (checkEvents?.data?.length ?? 0) === 0 && (
                  <div className="text-muted small" data-testid="check-history-empty">
                    Chưa có lịch sử thao tác chi tiết (áp dụng từ phiếu tạo sau ngày triển khai).
                  </div>
                )}

                {!eventsLoading && (checkEvents?.data?.length ?? 0) > 0 && (
                  <Table size="sm" bordered responsive className="mb-0" data-testid="check-history-table">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 150 }}>Thời điểm</th>
                        <th style={{ width: 130 }}>Ai</th>
                        <th style={{ width: 120 }}>Thao tác</th>
                        <th>Chi tiết</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checkEvents.data.map((e) => (
                        <tr key={e.id}>
                          <td className="small">{fmtDate(e.created_at)}</td>
                          <td className="small fw-medium">{e.actor?.full_name || '—'}</td>
                          <td>
                            <Badge bg={CHECK_ACTION_BG[e.action_type] ?? 'secondary'}>
                              {e.action_label || e.action_type}
                            </Badge>
                          </td>
                          <td className="small text-muted">{describeCheckEvent(e)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}

                {/* Vế sếp nhấn mạnh nhất: NV trực ca mà KHÔNG hề đụng vào phiếu. */}
                {!eventsLoading && noInteraction?.applicable && noInteraction.employees?.length > 0 && (
                  <Alert variant="danger" className="py-2 mt-3 mb-0" data-testid="no-interaction-alert">
                    <strong>⚠️ NV trực ca nhưng KHÔNG tương tác với phiếu ({noInteraction.employees.length})</strong>
                    <ul className="mb-0 mt-1 small">
                      {noInteraction.employees.map((emp) => (
                        <li key={emp.employee_id}>
                          {emp.full_name} — ca {fmtDate(emp.shift_start)} → {fmtDate(emp.shift_end)}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {!eventsLoading && noInteraction?.applicable && (noInteraction.employees?.length ?? 0) === 0 && (
                  <div className="small text-success mt-3" data-testid="no-interaction-none">
                    ✓ Mọi NV trực ca trong khung giờ phiếu mở đều có tương tác với phiếu.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {zaloResult && (
          <Alert
            variant={zaloResult.failed ? 'danger' : 'success'}
            className="mt-3 mb-0 py-2"
            data-testid="zalo-result"
          >
            {zaloResult.results.map((r) => (
              <div key={r.message_id}>
                {r.branch_name} → {r.phone}:{' '}
                {r.ok ? 'đã gửi' : <strong>THẤT BẠI — {r.error}</strong>}
              </div>
            ))}
          </Alert>
        )}
      </div>

      {/* XEM TRƯỚC bắt buộc: hiện nội dung thật + SĐT thật trước khi có nút gửi */}
      <Modal show={!!zaloPreview} onHide={() => setZaloPreview(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Xem trước tin đơn đặt hàng</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="py-2">
            Gửi tới SĐT Zalo <strong data-testid="zalo-preview-phone">{zaloPreview?.target_phone || '(NCC chưa có SĐT Zalo)'}</strong>{' '}
            của NCC <strong>{zaloPreview?.supplier?.name}</strong>.
          </Alert>
          {(zaloPreview?.messages || []).map((m) => (
            <Card className="mb-2" key={m.branch_id}>
              <Card.Header className="py-2 small fw-semibold bg-white">{m.branch_name}</Card.Header>
              <Card.Body className="py-2">
                <pre
                  className="mb-0 small"
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                  data-testid={`zalo-preview-${m.branch_id}`}
                >
                  {m.content}
                </pre>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setZaloPreview(null)} disabled={zaloSending}>Huỷ</Button>
          <Button
            variant="danger"
            data-testid="btn-zalo-confirm-send"
            disabled={zaloSending || !zaloPreview?.target_phone}
            onClick={handleZaloSend}
          >
            {zaloSending ? 'Đang gửi...' : 'Xác nhận & gửi'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
